import { Notification } from "components/Notification";
import { AnchorButtonTextOnly } from "components/ButtonTextOnly/AnchorButtonTextOnly";
import { IconArrowRight } from "components/Icons/IconArrowRight";

import {
  RateOption,
  toRateOption,
  hasTimeOfDay,
  toDescriptionInSentence,
} from "../cron/optionsRate";

import type { ActiveContext } from "shared/ContextSwitcher/store";
import type { ExecutionTiersData } from "shared/SelectPerformance/useGetPerformanceTiers";
import type { FormState } from "../FormState";
import type { Cost } from "./TierAndCost";
import { CronString, getNextCronRun } from "../cron/cronUtils";
import { format } from "date-fns-tz";

interface Props {
  contextOwner: ActiveContext;
  performanceTiers: ExecutionTiersData[];

  state: FormState;

  cost: Cost;
}

export function CostNotificationForQuery(props: Props) {
  const {
    state,
    cost: { planMonthlyCredits, monthlyCredits },
  } = props;
  const periodOption = toRateOption(state.cronString);

  if (periodOption === undefined) {
    return null;
  }

  const monthlyCostInPercent = Math.round(
    (monthlyCredits / planMonthlyCredits) * 100
  );

  if (monthlyCredits > planMonthlyCredits) {
    return (
      <Notification
        level="warning"
        title="Not enough credits to run this query"
        description={
          <>
            This query's execution cost will be up to{" "}
            <strong>{monthlyCredits} credits per month</strong>. This amount
            exceeds your subscription's total credit allowance. To run this
            query efficiently, please{" "}
            <strong>toggle your plan's "extra credits limit" off</strong>, or
            increase your plan's extra credits limit amount.
          </>
        }
        actions={
          <>
            <AnchorButtonTextOnly
              size="M"
              theme="tertiary"
              href={getSubscriptionSettingsUrl(props.contextOwner)}
              target="_blank"
            >
              Manage subscription
              <IconArrowRight />
            </AnchorButtonTextOnly>
          </>
        }
        dismissable={false}
      />
    );
  }

  return (
    <Notification
      level="info"
      title={renderTitle(periodOption, state.cronString)}
      description={
        <>
          The estimated execution cost is{" "}
          <strong>{monthlyCredits} credits</strong> per month (
          {monthlyCostInPercent}% of your monthly quota).
        </>
      }
      dismissable={false}
    />
  );
}

function renderTitle(option: RateOption, cronString: CronString) {
  const nextRun = getNextCronRun(cronString);

  if (hasTimeOfDay(cronString)) {
    return (
      <>
        This query will run continuously every {toDescriptionInSentence(option)}
        {nextRun && (
          <>
            {" "}
            between {format(nextRun, "HH:mm")}&nbsp;-&nbsp;
            {format(new Date(nextRun).getTime() + 30 * 60_000, "HH:mm")}
            &nbsp;UTC
          </>
        )}
      </>
    );
  }

  return (
    <>
      This query will run continuously every {toDescriptionInSentence(option)}
      {nextRun && (
        <>
          .<br />
          The next run will be at {format(nextRun, "HH:mm")} UTC.
        </>
      )}
    </>
  );
}

function getSubscriptionSettingsUrl(contextOwner: ActiveContext) {
  switch (contextOwner.type) {
    case "user":
      return "/settings/subscription";
    case "team":
      return `/settings/teams/manage/${contextOwner.handle}/subscription`;
  }
}
